import EnglishData from "./EnglishData.json";
import countriesData from "./countriesData.json";
import { Country } from "./types";

// A "lens" re-colours the world map by one dimension of the dataset, turning
// 206 separate country pages into a single picture you can read at a glance.
//
// The source data is free prose ("Majority Catholic (62.9%), growth of other
// religions such as Evangelism..."), so every lens classifies by keyword and
// keeps an explicit "mixed / no data" bucket. A country is never forced into
// a category the text doesn't actually support.

export interface LensCategory {
  id: string;
  color: string;
  es: string;
  en: string;
}

export interface LearnLens {
  slug: string;
  /**
   * The slug an English reader sees in the URL. `slug` stays the canonical
   * id — `related` arrays and every internal lookup use it — so translating
   * the URLs never moves anything that points at an item.
   */
  slugEn: string;
  emoji: string;
  /** Dataset field(s) this lens reads, in priority order. */
  fields: string[];
  /**
   * Reads the value to classify when it isn't a top-level string — the
   * measured indicators live one level down, inside the "Indexes" block.
   * Defaults to the first non-empty entry in `fields`.
   */
  read?: (info: Record<string, unknown>) => string | undefined;
  categories: LensCategory[];
  /** Returns the category id for a country, or undefined for "no data". */
  classify: (value: string, country: Country) => string | undefined;
  es: { title: string; summary: string; body: string[]; sourceNote: string };
  en: { title: string; summary: string; body: string[]; sourceNote: string };
}

const OTHER: LensCategory = {
  id: "other",
  color: "#64748b",
  es: "Otro / mixto",
  en: "Other / mixed",
};

// Priority matching: the first rule that matches wins, so more specific terms
// must be listed before broader ones. Right when the categories are mutually
// exclusive ("Islamic republic" must beat "republic").
const firstMatch = (
  value: string,
  rules: Array<[string, RegExp]>
): string | undefined => rules.find(([, re]) => re.test(value))?.[0];

// Position matching: whichever category is mentioned *earliest* in the text
// wins. These fields are prose that leads with the majority and then lists
// minorities ("Majority Catholic (62.9%), growth of other religions such as
// Evangelism...", "English (official), though French is also spoken"), so
// priority matching would happily classify a country by one of its
// minorities. Order of the rules is irrelevant here.
const earliestMatch = (
  value: string,
  rules: Array<[string, RegExp]>,
  fallback: string
): string => {
  let bestId = fallback;
  let bestIndex = Number.POSITIVE_INFINITY;

  rules.forEach(([id, re]) => {
    const index = value.search(re);
    if (index >= 0 && index < bestIndex) {
      bestIndex = index;
      bestId = id;
    }
  });

  return bestId;
};

export const LEARN_LENSES: LearnLens[] = [
  {
    slug: "religiones",
    slugEn: "religions",
    emoji: "🕌",
    fields: ["Religions"],
    categories: [
      { id: "christian", color: "#8b5cf6", es: "Cristianismo", en: "Christianity" },
      { id: "muslim", color: "#10b981", es: "Islam", en: "Islam" },
      { id: "buddhist", color: "#f59e0b", es: "Budismo", en: "Buddhism" },
      { id: "hindu", color: "#ef4444", es: "Hinduismo", en: "Hinduism" },
      { id: "jewish", color: "#3b82f6", es: "Judaísmo", en: "Judaism" },
      { id: "secular", color: "#e879f9", es: "Sin religión mayoritaria", en: "No religious majority" },
      OTHER,
    ],
    classify: (value) =>
      earliestMatch(
        value,
        [
          ["secular", /non-religious|no religion|atheis|irreligio|sin religión|ateo/i],
          ["muslim", /muslim|islam|musulman|sunni|shia|suní|chií/i],
          ["hindu", /hindu|hindú/i],
          ["buddhist", /buddh|budis/i],
          ["jewish", /jewish|judaísmo|judaism|judío/i],
          [
            "christian",
            /christian|catholic|orthodox|protestant|evangel|cristian|católic|ortodox/i,
          ],
        ],
        "other"
      ),
    es: {
      title: "El mapa de las religiones",
      summary:
        "Qué religión predomina en cada país, y por qué el mapa se parece tanto al de los imperios de hace 500 años.",
      body: [
        "Las grandes religiones no se repartieron por el mundo al azar: su distribución actual sigue de cerca las rutas comerciales, las conquistas y los imperios coloniales que las trasladaron. El cristianismo llega a América y a buena parte de África subsahariana con la colonización europea; el islam se expande por el norte de África, Asia central y el sudeste asiático siguiendo rutas de comercio y conquista; y el budismo se difunde desde la India hacia el este.",
        "Mirar el mapa completo de un tirón hace visible algo que país por país no se nota: las fronteras religiosas casi nunca coinciden con las fronteras políticas, y buena parte de los conflictos internos del mundo actual ocurren justamente donde no coinciden.",
      ],
      sourceNote:
        "Clasificado a partir del texto libre del dataset, quedándose con la religión declarada como mayoritaria. Los países con descripciones mixtas o sin dato quedan en gris.",
    },
    en: {
      title: "The map of religions",
      summary:
        "Which religion predominates in each country — and why the map looks so much like one of 500-year-old empires.",
      body: [
        "The major religions weren't scattered across the world at random: their distribution today closely tracks the trade routes, conquests and colonial empires that carried them. Christianity reaches the Americas and much of sub-Saharan Africa with European colonization; Islam spreads across North Africa, Central Asia and Southeast Asia along routes of trade and conquest; Buddhism diffuses eastward from India.",
        "Seeing the whole map at once reveals something you can't notice country by country: religious boundaries almost never line up with political ones, and a good share of today's internal conflicts happen exactly where they don't.",
      ],
      sourceNote:
        "Classified from the dataset's free text, taking the religion it names as the majority. Countries with mixed descriptions or no data stay grey.",
    },
  },
  {
    slug: "idiomas",
    slugEn: "languages",
    emoji: "🗨️",
    fields: ["Language"],
    categories: [
      { id: "english", color: "#3b82f6", es: "Inglés", en: "English" },
      { id: "spanish", color: "#f59e0b", es: "Español", en: "Spanish" },
      { id: "french", color: "#8b5cf6", es: "Francés", en: "French" },
      { id: "arabic", color: "#10b981", es: "Árabe", en: "Arabic" },
      { id: "portuguese", color: "#ef4444", es: "Portugués", en: "Portuguese" },
      { id: "russian", color: "#ec4899", es: "Ruso", en: "Russian" },
      OTHER,
    ],
    classify: (value) =>
      earliestMatch(
        value,
        [
          ["arabic", /arabic|árabe/i],
          ["spanish", /spanish|español|castellano/i],
          ["portuguese", /portuguese|portugués/i],
          ["french", /french|francés/i],
          ["russian", /russian|ruso/i],
          ["english", /english|inglés/i],
        ],
        "other"
      ),
    es: {
      title: "Las lenguas que cruzaron océanos",
      summary:
        "Seis idiomas europeos son oficiales en buena parte del planeta. El mapa muestra exactamente hasta dónde llegó cada imperio.",
      body: [
        "Que el francés sea oficial en más de veinte países africanos, o el portugués en Brasil, Angola y Mozambique, no dice nada sobre esas lenguas y todo sobre quién colonizó qué. Este mapa es, en la práctica, el mapa de los imperios europeos superpuesto al presente.",
        "Hay una consecuencia que sigue operando hoy: la lengua administrativa de muchos países no es la lengua materna de la mayoría de su población, lo que condiciona el acceso a la educación, a la justicia y al Estado.",
      ],
      sourceNote:
        "Toma el primer idioma oficial mencionado en el dataset. Muchos países tienen varias lenguas oficiales; el mapa muestra solo la primera.",
    },
    en: {
      title: "The languages that crossed oceans",
      summary:
        "Six European languages are official across much of the planet. The map shows exactly how far each empire reached.",
      body: [
        "That French is official in more than twenty African countries, or Portuguese in Brazil, Angola and Mozambique, says nothing about those languages and everything about who colonized what. This map is, in practice, the map of Europe's empires laid over the present.",
        "One consequence still plays out today: in many countries the language of administration isn't the mother tongue of most of the population, which shapes access to education, to the courts and to the state itself.",
      ],
      sourceNote:
        "Takes the first official language named in the dataset. Many countries have several; the map shows only the first.",
    },
  },
  {
    slug: "formas-de-gobierno",
    slugEn: "government-types",
    emoji: "👑",
    fields: ["Government Type", "Type of Government and/or Monarchy"],
    categories: [
      { id: "republic", color: "#3b82f6", es: "República", en: "Republic" },
      { id: "monarchy", color: "#f59e0b", es: "Monarquía", en: "Monarchy" },
      { id: "federal", color: "#10b981", es: "República federal", en: "Federal republic" },
      { id: "single-party", color: "#ef4444", es: "Partido único", en: "Single-party state" },
      { id: "theocracy", color: "#8b5cf6", es: "Teocracia", en: "Theocracy" },
      OTHER,
    ],
    classify: (value) =>
      firstMatch(value, [
        ["theocracy", /theocra|teocrá|islamic republic|república islámica/i],
        ["single-party", /single-party|one-party|communist|partido único|comunista/i],
        ["monarchy", /monarch|kingdom|emirate|sultanate|monarquía|reino|emirato|sultanato/i],
        ["federal", /federal/i],
        ["republic", /republic|república/i],
        ["other", /./],
      ]),
    es: {
      title: "Cómo se gobierna el mundo",
      summary:
        "Repúblicas, monarquías, federaciones y estados de partido único, vistos todos juntos en un solo mapa.",
      body: [
        "La república es hoy la forma de gobierno dominante, pero eso es muy reciente: hace apenas un siglo la mayoría de la humanidad vivía bajo alguna forma de monarquía o de dominio imperial. El mapa muestra dónde sobrevivieron las coronas, y de qué tipo.",
        "Ojo con una distinción clave que el mapa no puede mostrar: la forma de gobierno no dice cuán democrático es un país. Hay monarquías con parlamentos plenamente democráticos y repúblicas sin elecciones competitivas.",
      ],
      sourceNote:
        "Clasificado por palabras clave sobre el tipo de gobierno declarado en el dataset.",
    },
    en: {
      title: "How the world is governed",
      summary:
        "Republics, monarchies, federations and single-party states, all seen together on one map.",
      body: [
        "The republic is the dominant form of government today, but that's very recent: barely a century ago most of humanity lived under some form of monarchy or imperial rule. The map shows where the crowns survived, and of what kind.",
        "Watch for a key distinction the map can't show: the form of government doesn't tell you how democratic a country is. There are monarchies with fully democratic parliaments, and republics with no competitive elections.",
      ],
      sourceNote:
        "Classified by keyword from the government type declared in the dataset.",
    },
  },
  {
    slug: "monedas",
    slugEn: "currencies",
    emoji: "💸",
    fields: ["Currency"],
    categories: [
      { id: "usd", color: "#10b981", es: "Dólar estadounidense", en: "US dollar" },
      { id: "eur", color: "#3b82f6", es: "Euro", en: "Euro" },
      { id: "pegged-franc", color: "#8b5cf6", es: "Franco CFA", en: "CFA franc" },
      { id: "own", color: "#f59e0b", es: "Moneda propia", en: "Own currency" },
      OTHER,
    ],
    classify: (value) =>
      firstMatch(value, [
        ["pegged-franc", /cfa/i],
        ["eur", /euro \(eur\)|\(eur\)|euro/i],
        ["usd", /us dollar|u\.s\. dollar|dólar estadounidense|\(usd\)/i],
        ["own", /./],
      ]),
    es: {
      title: "Quién emite su propio dinero",
      summary:
        "No todos los países tienen moneda propia: algunos comparten una, otros usan directamente la de otro país.",
      body: [
        "Emitir moneda propia es uno de los atributos clásicos de la soberanía, pero muchos países eligen —o aceptan— no hacerlo. Veinte países comparten el euro; varios de América Latina y el Caribe usan directamente el dólar estadounidense; y catorce países africanos usan el franco CFA, una moneda heredada del período colonial francés y atada al euro.",
        "La contrapartida siempre es la misma: ceder moneda propia significa ganar estabilidad y perder la capacidad de responder a una crisis con política monetaria propia.",
      ],
      sourceNote:
        "Clasificado por la moneda declarada en el dataset. Los países que usan el dólar solo como moneda secundaria pueden aparecer con su moneda propia.",
    },
    en: {
      title: "Who issues their own money",
      summary:
        "Not every country has its own currency: some share one, others simply use another country's.",
      body: [
        "Issuing your own currency is one of the classic attributes of sovereignty, yet many countries choose — or accept — not to. Twenty countries share the euro; several in Latin America and the Caribbean use the US dollar outright; and fourteen African countries use the CFA franc, a currency inherited from the French colonial period and pegged to the euro.",
        "The trade-off is always the same: giving up your own currency buys stability and costs you the ability to answer a crisis with monetary policy of your own.",
      ],
      sourceNote:
        "Classified by the currency declared in the dataset. Countries where the dollar is only a secondary tender may appear under their own currency.",
    },
  },
  {
    // The first lens that reads a measured number rather than prose, so the
    // buckets are ranges and there is nothing to disambiguate: a country is
    // in a band or it has no figure at all.
    slug: "emisiones",
    slugEn: "emissions",
    emoji: "🌡️",
    fields: ["Indexes"],
    read: (info) => {
      const block = info["Indexes"];
      if (typeof block !== "object" || block === null) return undefined;
      const value = (block as Record<string, unknown>)["CO₂ emissions"];
      return typeof value === "string" ? value : undefined;
    },
    categories: [
      { id: "b1", color: "#fde68a", es: "Menos de 1 t", en: "Under 1 t" },
      { id: "b2", color: "#fbbf24", es: "1 a 3 t", en: "1 to 3 t" },
      { id: "b3", color: "#f97316", es: "3 a 6 t", en: "3 to 6 t" },
      { id: "b4", color: "#dc2626", es: "6 a 12 t", en: "6 to 12 t" },
      { id: "b5", color: "#7f1d1d", es: "Más de 12 t", en: "Over 12 t" },
    ],
    classify: (value) => {
      // "4.0 tonnes per person per year (2024)" — the English dataset writes
      // the decimal with a dot, so the leading number parses directly.
      const tonnes = Number.parseFloat(value);
      if (!Number.isFinite(tonnes)) return undefined;
      if (tonnes < 1) return "b1";
      if (tonnes < 3) return "b2";
      if (tonnes < 6) return "b3";
      if (tonnes < 12) return "b4";
      return "b5";
    },
    es: {
      title: "Cuánto carbono emite cada persona",
      summary:
        "El mismo mapa que contás por países se ve completamente distinto cuando lo pintás por lo que emite cada habitante.",
      body: [
        "Este mapa mide toneladas de CO₂ por persona y por año, no el total del país. La diferencia lo cambia todo: China es el mayor emisor del planeta en total, pero por habitante está cerca del promedio europeo, y la India —el tercer emisor mundial— queda entre los países más claros del mapa. Los tonos más oscuros no son los países más grandes sino los más ricos en energía: los del Golfo, y varios exportadores de petróleo y gas.",
        "La mediana mundial está en 2,6 toneladas por persona. Es un número más bajo de lo que suele suponerse, y esconde una distancia enorme entre los extremos: hay países que emiten menos de una décima de tonelada por habitante y otros que superan las veinte.",
        "Esa brecha es exactamente la que está en el centro de las negociaciones climáticas. Los países que hoy aparecen oscuros llevan décadas emitiendo; muchos de los claros sufren los efectos más duros del calentamiento sin haber contribuido casi nada a causarlo. Ningún mapa de un solo año muestra esa deuda acumulada, pero sí muestra quién sigue emitiendo hoy.",
        "Mirá también el contraste con países ricos de baja emisión: Francia, Suecia o Uruguay están muy por debajo de sus pares en ingreso, porque su electricidad es nuclear o renovable. La riqueza empuja las emisiones hacia arriba, pero no las determina.",
      ],
      sourceNote:
        "Banco Mundial, emisiones de CO₂ per cápita, dato más reciente disponible por país. 172 de los 206 territorios tienen cifra; el resto aparece sin datos.",
    },
    en: {
      title: "How much carbon each person emits",
      summary:
        "The same map you count in countries looks completely different once you paint it by what each inhabitant emits.",
      body: [
        "This map measures tonnes of CO₂ per person per year, not a country's total. The difference changes everything: China is the planet's largest emitter outright, yet per head it sits near the European average, and India — the world's third largest — comes out among the palest countries on the map. The darkest shades aren't the biggest countries but the most energy-rich: the Gulf states, and several oil and gas exporters.",
        "The world median is 2.6 tonnes per person. That is lower than most people assume, and it hides an enormous distance between the extremes: some countries emit less than a tenth of a tonne per inhabitant while others pass twenty.",
        "That gap is exactly what sits at the centre of climate negotiations. The countries showing dark here have been emitting for decades; many of the pale ones take the hardest effects of warming without having done much to cause it. No single-year map can show that accumulated debt, but it does show who is still emitting now.",
        "Look too at the rich countries with low emissions: France, Sweden and Uruguay sit well below their income peers, because their electricity is nuclear or renewable. Wealth pushes emissions up, but it doesn't settle them.",
      ],
      sourceNote:
        "World Bank, CO₂ emissions per capita, most recent available year per country. 172 of the 206 territories carry a figure; the rest show as no data.",
    },
  },
  {
    // The best-covered indicator in the dataset: 199 of 206 territories.
    slug: "esperanza-de-vida",
    slugEn: "life-expectancy",
    emoji: "🕰️",
    fields: ["Indexes"],
    read: (info) => {
      const block = info["Indexes"];
      if (typeof block !== "object" || block === null) return undefined;
      const value = (block as Record<string, unknown>)["Life expectancy"];
      return typeof value === "string" ? value : undefined;
    },
    // Diverging rather than sequential, because this indicator has a
    // meaningful middle: the world median sits at 73.9 years, inside the
    // third band.
    categories: [
      { id: "b1", color: "#c2410c", es: "Menos de 60 años", en: "Under 60 years" },
      { id: "b2", color: "#f97316", es: "60 a 70", en: "60 to 70" },
      { id: "b3", color: "#fbbf24", es: "70 a 75", en: "70 to 75" },
      { id: "b4", color: "#84cc16", es: "75 a 80", en: "75 to 80" },
      { id: "b5", color: "#15803d", es: "80 o más", en: "80 or more" },
    ],
    classify: (value) => {
      // "77.5 years (2024)" — the English dataset writes the decimal with a
      // dot, so the leading number parses directly.
      const years = Number.parseFloat(value);
      if (!Number.isFinite(years)) return undefined;
      if (years < 60) return "b1";
      if (years < 70) return "b2";
      if (years < 75) return "b3";
      if (years < 80) return "b4";
      return "b5";
    },
    es: {
      title: "Cuántos años se vive en cada país",
      summary:
        "La distancia entre los extremos es de más de treinta años de vida, y el mapa muestra dónde está esa frontera.",
      body: [
        "La esperanza de vida al nacer es la cantidad de años que viviría una persona nacida hoy si las condiciones actuales se mantuvieran durante toda su vida. No es una predicción sobre nadie en particular: es una foto del presente expresada en años, y por eso cae de golpe durante una guerra o una epidemia y se recupera después.",
        "El mapa muestra una frontera nítida. Los tonos más oscuros se concentran en Europa occidental, Japón, Corea, Australia y Canadá, todos por encima de los 80 años. Los más claros están casi todos en África subsahariana, varios por debajo de los 60. La diferencia entre los extremos supera los treinta años: alguien nacido en un país y alguien nacido en otro tienen, en promedio, media vida de diferencia.",
        "Conviene entender qué mueve el número, porque no es lo que suele suponerse. Lo que más empuja la esperanza de vida hacia arriba no es que los ancianos vivan más, sino que menos chicos mueran: como el cálculo promedia todas las edades, cada muerte infantil pesa muchísimo. Por eso el agua potable, las vacunas y la atención del parto mueven este indicador más que casi cualquier avance de la medicina de alta complejidad.",
        "Y por eso vale compararlo con el mapa del PBI per cápita, que no se le superpone del todo. Costa Rica y Cuba superan a varios países bastante más ricos; Estados Unidos está por debajo de casi toda Europa occidental pese a gastar más en salud que cualquier otro país. El dinero ayuda, pero lo que decide es cómo se reparte y en qué se gasta.",
      ],
      sourceNote:
        "Banco Mundial, esperanza de vida al nacer, dato más reciente disponible por país. 199 de los 206 territorios tienen cifra; el resto aparece sin datos.",
    },
    en: {
      title: "How long a life lasts in each country",
      summary:
        "The gap between the extremes is more than thirty years of life, and the map shows where that line falls.",
      body: [
        "Life expectancy at birth is how long someone born today would live if current conditions held for their whole life. It is not a prediction about any individual: it is a photograph of the present expressed in years, which is why it drops sharply during a war or an epidemic and recovers afterwards.",
        "The map shows a sharp line. The darkest shades cluster in Western Europe, Japan, Korea, Australia and Canada, all above 80 years. The palest are almost all in sub-Saharan Africa, several below 60. The gap between the extremes passes thirty years: someone born in one country and someone born in another are, on average, half a lifetime apart.",
        "It is worth understanding what moves the number, because it isn't what people assume. What pushes life expectancy up most is not the old living longer but fewer children dying: because the calculation averages across all ages, every infant death weighs enormously. Clean water, vaccines and care at birth move this indicator more than almost any advance in high-complexity medicine.",
        "And that is why it repays comparing with the map of GDP per capita, which it does not quite overlay. Costa Rica and Cuba beat several considerably richer countries; the United States sits below almost all of Western Europe despite spending more on health than any other country. Money helps, but what decides is how it is shared and what it is spent on.",
      ],
      sourceNote:
        "World Bank, life expectancy at birth, most recent available year per country. 199 of the 206 territories carry a figure; the rest show as no data.",
    },
  },
];

export const findLearnLens = (slug?: string) =>
  LEARN_LENSES.find((lens) => lens.slug === slug || lens.slugEn === slug);

export interface LensResult {
  /** English country name → fill colour. */
  colors: Record<string, string>;
  /** Category id → how many countries fall in it. */
  counts: Record<string, number>;
  noData: number;
}

export const applyLens = (lens: LearnLens): LensResult => {
  const dataset = EnglishData as Record<string, Record<string, unknown>>;
  const colorById = new Map(lens.categories.map((c) => [c.id, c.color]));
  const colors: Record<string, string> = {};
  const counts: Record<string, number> = {};
  let noData = 0;

  (countriesData as Country[]).forEach((country) => {
    if (country["not-a-country"]) return;
    const englishName = country["english-name"];
    const info = dataset[englishName];

    const raw =
      lens.read?.(info ?? {}) ??
      lens.fields
        .map((field) => info?.[field])
        .find(
          (value): value is string =>
            typeof value === "string" && !!value.trim()
        );

    if (!raw) {
      noData += 1;
      return;
    }

    const categoryId = lens.classify(raw, country);
    const color = categoryId ? colorById.get(categoryId) : undefined;
    if (!categoryId || !color) {
      noData += 1;
      return;
    }

    colors[englishName] = color;
    counts[categoryId] = (counts[categoryId] ?? 0) + 1;
  });

  return { colors, counts, noData };
};
